<div class="container mx-auto px-4 py-6">
  <!-- Header -->
  <div class="mb-6">
    <button 
      (click)="cancel()"
      class="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
      <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
      </svg>
      Volver a roles
    </button>
    <h1 class="text-2xl font-bold text-gray-900">
      {{ isEditMode ? 'Editar Rol' : 'Nuevo Rol' }}
    </h1>
    <p class="text-gray-600 mt-1">
      {{ isEditMode ? 'Modifica la información y permisos del rol' : 'Crea un nuevo rol y asigna sus permisos' }}
    </p>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="flex justify-center items-center py-12">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>

  <!-- Form -->
  <div *ngIf="!loading" class="space-y-6">
    <!-- Basic Info Card -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 class="text-lg font-semibold text-gray-900 mb-4">Información del Rol</h2>
      
      <form [formGroup]="form">
        <div class="max-w-md">
          <label for="name" class="block text-sm font-medium text-gray-700 mb-1">
            Nombre del Rol <span class="text-red-500">*</span>
          </label>
          <input 
            type="text" 
            id="name" 
            formControlName="name"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            [class.border-red-500]="form.get('name')?.invalid && form.get('name')?.touched"
            placeholder="Ej: Contador, Asistente, etc.">
          
          <div *ngIf="form.get('name')?.invalid && form.get('name')?.touched" class="mt-1 text-sm text-red-600">
            <span *ngIf="form.get('name')?.errors?.['required']">El nombre es requerido</span>
            <span *ngIf="form.get('name')?.errors?.['minlength']">Mínimo 2 caracteres</span>
            <span *ngIf="form.get('name')?.errors?.['maxlength']">Máximo 50 caracteres</span>
          </div>

          <p *ngIf="role?.isSystemRole" class="mt-2 text-sm text-yellow-600 flex items-center">
            <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
            </svg>
            Este es un rol del sistema, no se puede cambiar el nombre
          </p>
        </div>
      </form>
    </div>

    <!-- Permissions Card -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">Permisos</h2>
          <p class="text-sm text-gray-500">Selecciona los permisos que tendrá este rol</p>
        </div>
        <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          {{ selectedPermissionIds.size }} seleccionados
        </span>
      </div>

      <!-- Permission Groups -->
      <div *ngIf="allPermissionsGrouped" class="space-y-3">
        <div *ngFor="let group of allPermissionsGrouped.groups" 
             class="border border-gray-200 rounded-lg overflow-hidden">
          
          <!-- Module Header -->
          <div 
            (click)="toggleModule(group.module)"
            class="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
            <div class="flex items-center">
              <svg 
                class="w-5 h-5 text-gray-400 transition-transform mr-3"
                [class.rotate-90]="isModuleExpanded(group.module)"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
              <span class="font-medium text-gray-900">{{ group.moduleDisplayName }}</span>
              <span class="ml-2 text-sm text-gray-500">
                ({{ getSelectedCountInModule(group.module) }}/{{ group.permissions.length }})
              </span>
            </div>
            <div class="flex items-center gap-2" (click)="$event.stopPropagation()">
              <button 
                (click)="selectAllInModule(group.module)"
                class="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50">
                Todos
              </button>
              <button 
                (click)="deselectAllInModule(group.module)"
                class="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-100">
                Ninguno
              </button>
            </div>
          </div>

          <!-- Permissions List -->
          <div *ngIf="isModuleExpanded(group.module)" class="p-4 bg-white">
            <!-- Info: Dependencia de permisos -->
            <div class="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center">
              <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
              </svg>
              Al seleccionar Crear, Editar o Eliminar, se requiere obligatoriamente el permiso de Lectura.
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <label 
                *ngFor="let permission of group.permissions"
                class="flex items-start p-3 rounded-lg border cursor-pointer transition-colors"
                [class.border-blue-500]="isPermissionSelected(permission.id)"
                [class.bg-blue-50]="isPermissionSelected(permission.id)"
                [class.border-gray-200]="!isPermissionSelected(permission.id) && !isReadPermissionRequired(permission.id)"
                [class.hover:border-blue-300]="!isReadPermissionRequired(permission.id)"
                [class.hover:bg-blue-50]="!isReadPermissionRequired(permission.id)"
                [class.border-amber-400]="isReadPermissionRequired(permission.id)"
                [class.bg-amber-50]="isReadPermissionRequired(permission.id)"
                [class.cursor-not-allowed]="isReadPermissionRequired(permission.id)">
                <input 
                  type="checkbox"
                  [checked]="isPermissionSelected(permission.id)"
                  (change)="togglePermission(permission.id)"
                  [disabled]="isReadPermissionRequired(permission.id)"
                  class="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50">
                <div class="ml-3 flex-1">
                  <div class="flex items-center">
                    <span class="text-sm font-medium text-gray-900">{{ permission.action }}</span>
                    <svg *ngIf="isReadPermissionRequired(permission.id)" 
                         class="w-4 h-4 ml-1 text-amber-500" 
                         fill="currentColor" viewBox="0 0 20 20"
                         title="Requerido por permisos de escritura">
                      <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                  <p class="text-xs text-gray-500">{{ permission.description }}</p>
                  <p *ngIf="isReadPermissionRequired(permission.id)" class="text-xs text-amber-600 mt-1">
                    Requerido por permisos de escritura
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- No permissions message -->
      <div *ngIf="!allPermissionsGrouped || allPermissionsGrouped.groups.length === 0" 
           class="text-center py-8 text-gray-500">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <p class="mt-2">No hay permisos disponibles</p>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-end gap-3 pt-4">
      <button 
        (click)="cancel()"
        class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
        Cancelar
      </button>
      <button 
        (click)="save()"
        [disabled]="saving || form.invalid"
        class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="saving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {{ isEditMode ? 'Guardar Cambios' : 'Crear Rol' }}
      </button>
    </div>
  </div>
</div>

